import type { ApiClient, SocialAppearanceOptions, SocialAvatarChoice, SocialGridDocument, SocialGroup, SocialMe, SocialMediaMetadata, SocialMonth, SocialOwnerGrid, SocialPostPage, SocialProfile, SocialSubmission, SocialUploadStatus } from '@daily/contracts/api';
import type { UploadTransport } from '../uploads/transport.js';
import { createV1Auth } from './auth.js';
import type { AuthRuntime } from './auth.js';
import { codeOf, currentRevisionOf, uiErrorOf } from './errors.js';
import { GridQueue } from './grid-queue.js';
import { colourOf, documentFromWidgets, isUuid, mediaOf, memberOf, pictureOf, sameDocument, widgetsFromDocument } from './mapping.js';
import type { AddressOf } from './mapping.js';
import { MediaAddresses } from './media-addresses.js';
import type { AuthSource, Capabilities, DataSource, DayEntry, DayMark, ISODate, MediaRef, Member, Picture, PostDoc, PresetPicture, ProfileDoc, Session, UiError, Widget } from './types.js';

export interface V1BackendOptions {
  readonly api: ApiClient;
  readonly transport: UploadTransport;
  readonly auth: AuthRuntime;
  /** Whether the backend offers Google sign-in. */
  readonly google: boolean;
  /** The device's time zone: the posting zone given at sign-up. */
  readonly timezone: () => string;
  /** Fresh UUIDs: idempotency keys and the ids of new widgets. */
  readonly key: () => string;
  readonly now: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  /** A local address that shows a just-picked file while the backend processes it. */
  readonly preview: (file: File) => string | null;
  /** Clears recorded voice kept on this device (the voice lab), as every sign-out does. */
  readonly purgeVoice: () => Promise<void>;
  /** How long a save waits for the backend to publish before it reports success anyway (90 s). */
  readonly publishWaitMs?: number;
  /** Makes a photo that is larger than the app can show smaller before it is sent, or answers the file itself. */
  readonly fit?: (file: File) => Promise<File>;
  /**
   * Where this tab keeps the ids of uploads it has not finished, across a reload: a page that is reloaded or thrown
   * away while it sends a file cannot cancel that upload itself. Ids only, never an upload address.
   */
  readonly unfinishedUploads?: { read(): readonly string[]; write(ids: readonly string[]): void };
}

/**
 * Everything the backend can do for v1 (owner decisions of 3 October 2026): one calendar, the
 * Everyone group, for every read and every post. Songs have no source.
 */
const CAPABILITIES: Capabilities = {
  postLayout: true, editPost: true, deletePost: true, video: true, profileGrid: true, profileDescription: true,
  rename: true, changeColour: true, pictureUpload: true, presetPictures: true, songs: false, colourLines: true,
};
/** GIFs since 4 October 2026 (owner), for posts and the profile grid; a profile picture stays JPEG, PNG or WebP. */
const IMAGE_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const VIDEO_TYPES: readonly string[] = ['video/mp4', 'video/webm', 'video/quicktime'];
const IMAGE_MAX = 15 * 1024 * 1024;
/** A photo larger than the backend takes can still be picked: it is made smaller first. Beyond this it is not even opened. */
const IMAGE_OPENED_MAX = 4 * IMAGE_MAX;
const VIDEO_MAX = 250 * 1024 * 1024;
const DAY_LIMIT = 42;
const READS_AT_ONCE = 6;
/** Files sent at the same time; the others wait their turn (06 client: at most two active transfers). */
const TRANSFERS_AT_ONCE = 2;
/**
 * The backend keeps a place for each upload it has not finished checking, and has three (08 media). One that was
 * just sent is checked a few seconds later, so a refusal for want of a place is asked again for this long first.
 */
const PLACE_WAITS_MS: readonly number[] = [1_000, 2_000, 3_000, 4_000, 5_000, 5_000];
const EMPTY: SocialGridDocument = { schemaVersion: 1, columns: 7, rows: 6, widgets: [] };

const fail = (kind: UiError['kind'], detail: string): UiError => ({ kind, detail });
const pad = (value: number) => String(value).padStart(2, '0');

/** The calendar date of an instant in a time zone, as 'YYYY-MM-DD'. Only a guess: the backend says which day is open. */
export function dayIn(instant: number, timezone: string): ISODate {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(instant));
    const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
  } catch { return new Date(instant).toISOString().slice(0, 10); }
}

/** Run `work` over `items`, at most `limit` at a time, keeping the order of the results. */
async function pool<T, R>(items: readonly T[], limit: number, work: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array<R>(items.length);
  let next = 0;
  const lane = async () => { while (next < items.length) { const at = next; next += 1; out[at] = await work(items[at] as T); } };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
  return out;
}

interface OwnGrid { readonly id: string; revision: number }

class BackendDataSource implements DataSource {
  readonly capabilities = CAPABILITIES;
  private readonly media: MediaAddresses;
  private readonly queue = new GridQueue();
  private transfers = 0;
  private readonly transferTurns: (() => void)[] = [];
  /** Uploads under way on this page, and ones that stopped without being cancelled (no connection, or an earlier load of this tab). */
  private readonly underWay = new Set<string>();
  private leftBehind: string[];
  private me: SocialMe | null = null;
  private group: SocialGroup | null = null;
  private appearance: SocialAppearanceOptions | null = null;
  private todayKey: ISODate | null = null;
  /** Month reads for a few seconds, so the session's month read also answers the first `getMonth`. */
  private readonly months = new Map<string, { at: number; value: SocialMonth }>();
  /** Who posted on each day in stripe order (Everyone's member order), for ordering a day's posts. */
  private readonly order = new Map<ISODate, readonly string[]>();
  /** Your daily grids by date, and the working revision each was last seen at. */
  private readonly grids = new Map<ISODate, OwnGrid>();
  private readonly gridLookups = new Map<ISODate, Promise<OwnGrid>>();
  /** Idempotency keys of daily grids being created, so a repeated create returns the same grid. */
  private readonly creationKeys = new Map<ISODate, string>();
  /** Working revisions of other grids (your profile grid). */
  private readonly revisions = new Map<string, number>();
  /** Local previews of files uploaded on this page, by asset id: shown until the backend's own address works. */
  private readonly previews = new Map<string, string>();
  private readonly address: AddressOf = (assetId, variant) => this.media.url(assetId, variant) ?? this.previews.get(assetId) ?? null;

  constructor(private readonly options: V1BackendOptions) {
    this.media = new MediaAddresses(options.api, options.now);
    this.leftBehind = (options.unfinishedUploads?.read() ?? []).filter(isUuid).slice(0, 12);
  }

  private get api() { return this.options.api; }

  /** Forget everything about the previous member (sign-out, sign-in as someone else). */
  reset(): void {
    this.me = null; this.group = null; this.appearance = null; this.todayKey = null;
    this.months.clear(); this.order.clear(); this.grids.clear(); this.gridLookups.clear(); this.creationKeys.clear(); this.revisions.clear();
    for (const url of this.previews.values()) if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    this.previews.clear(); this.media.clear(); this.queue.clear();
    this.leftBehind = []; this.underWay.clear(); this.keepUploads();
  }

  private async call<T>(run: () => Promise<T>): Promise<T> {
    try { return await run(); } catch (error) { throw uiErrorOf(error); }
  }

  // ------------------------------------------------------------ session

  getSession(): Promise<Session> {
    return this.call(async () => {
      const me = await this.api.request('socialMe', {});
      if (this.me && this.me.userId !== me.userId) this.reset();
      this.me = me;
      void this.cancelLeftBehind();
      const profile = me.profile;
      if (!profile) throw fail('profile_required', 'PROFILE_REQUIRED');
      const timezone = me.postingTimezone ?? this.options.timezone();
      const guess = dayIn(this.options.now(), timezone);
      const month = await this.month(guess.slice(0, 7));
      const group = await this.everyone();
      const today = month.creationDayKey ?? guess;
      const first = group.firstPostDayKey ?? dayIn(Date.parse(group.createdAt), timezone);
      const earliest = (first < today ? first : today).slice(0, 7);
      if (profile.avatarAssetId) await this.media.ensure([{ assetId: profile.avatarAssetId, variant: 'w640' }]);
      return {
        me: memberOf(profile, me.userId, this.address),
        members: [],
        today,
        earliestMonth: { year: Number(earliest.slice(0, 4)), month: Number(earliest.slice(5, 7)) },
        // Revision 1: the profile as confirming the address (or "Finish your account") made it, never saved since.
        firstRun: profile.revision === 1,
      };
    });
  }

  private requireMe(): SocialMe & { profile: SocialProfile } {
    const me = this.me;
    if (!me) throw fail('auth', 'no session read');
    if (!me.profile) throw fail('profile_required', 'PROFILE_REQUIRED');
    return me as SocialMe & { profile: SocialProfile };
  }

  /**
   * The Everyone group: every read and every post in v1 uses it ("Always Everyone"). Membership of it
   * is implicit, so the group list does not name it; a month read without a group is Everyone's, and
   * says so in its `group`, which is then kept and named in every later call.
   */
  private async everyone(): Promise<SocialGroup> {
    if (!this.group) await this.month(dayIn(this.options.now(), this.me?.postingTimezone ?? this.options.timezone()).slice(0, 7));
    if (!this.group) throw fail('server', 'everyone group missing');
    return this.group;
  }

  private async month(key: string): Promise<SocialMonth> {
    const cached = this.months.get(key);
    if (cached && this.options.now() - cached.at < 5_000) return cached.value;
    const known = this.group;
    const value = await this.api.request('socialMonth', { query: { month: key, filter: 'everyone', ...(known ? { groupId: known.id } : {}) } });
    if (!known) {
      if (value.group?.kind !== 'everyone') throw fail('server', 'everyone group missing');
      this.group = value.group;
    }
    this.months.set(key, { at: this.options.now(), value });
    if (value.creationDayKey) this.todayKey = value.creationDayKey;
    for (const day of value.days) this.order.set(day.dayKey, (day.accentSamples ?? []).map((sample) => sample.userId));
    return value;
  }

  // ------------------------------------------------------------ reads

  getMonth(ym: { year: number; month: number }) {
    return this.call(async () => {
      const month = await this.month(`${ym.year}-${pad(ym.month)}`);
      const days: Record<ISODate, DayMark> = {};
      for (const day of month.days) {
        if (day.contributors <= 0) continue;
        days[day.dayKey] = { count: day.contributors, samples: (day.accentSamples ?? []).map((sample) => ({ id: sample.userId, color: sample.accentColor.toLowerCase() })), mine: day.hasOwnPost ?? false };
      }
      return { year: ym.year, month: ym.month, days };
    });
  }

  getDay(date: ISODate) {
    return this.call(async () => {
      const group = await this.everyone();
      const meId = this.me?.userId ?? null;
      const cards: { postId: string }[] = [];
      let cursor: string | null = null;
      do {
        const page: SocialPostPage = await this.api.request('socialDayPosts', { path: { dayKey: date }, query: { filter: 'everyone', groupId: group.id, limit: 50, ...(cursor ? { cursor } : {}) } });
        for (const card of page.items) if (!cards.some((known) => known.postId === card.postId)) cards.push(card);
        cursor = page.nextCursor;
      } while (cursor && cards.length < DAY_LIMIT);
      // The day list carries no documents: each post is read on its own, a few at a time.
      const reads = await pool(cards.slice(0, DAY_LIMIT), READS_AT_ONCE, async (card) => {
        try { return await this.api.request('readSocialGrid', { path: { gridId: card.postId }, query: { groupId: group.id } }); }
        catch (error) { if (uiErrorOf(error).kind === 'not_found') return null; throw error; }
      });
      const visible = reads.filter((read): read is NonNullable<typeof read> => !!read && !!read.version);
      await this.media.ensure(visible.flatMap((read) => [
        ...mediaOf(read.version?.document ?? EMPTY, read.assets),
        ...(read.owner.avatarAssetId ? [{ assetId: read.owner.avatarAssetId, variant: 'w640' as const }] : []),
      ]));
      const entries: DayEntry[] = visible.map((read) => {
        const document = read.version?.document ?? EMPTY;
        return { memberId: read.owner.userId, author: memberOf(read.owner, meId, this.address), post: { items: widgetsFromDocument(document, read.assets, this.address), lines: document.lines ?? 'colour' } };
      });
      // Posts follow the stripe: the people the month sampled in its order, then the rest as the backend listed them.
      const order = this.order.get(date) ?? [];
      const rank = (id: string) => { const at = order.indexOf(id); return at < 0 ? order.length : at; };
      const sorted = entries.map((entry, index) => ({ entry, index })).sort((a, b) => rank(a.entry.memberId) - rank(b.entry.memberId) || a.index - b.index).map(({ entry }) => entry);
      return { date, entries: sorted };
    });
  }

  getProfile(memberId: string) {
    return this.call(async () => {
      const own = memberId === this.me?.userId;
      const profile = await this.api.request('getSocialProfile', { path: { userId: memberId } });
      let document: SocialGridDocument, assets: readonly SocialMediaMetadata[];
      if (own) {
        // Your own profile shows what you last shared, even while the backend is still publishing it.
        const working = await this.api.request('getSocialWorkingGrid', { path: { gridId: profile.gridId } });
        this.revisions.set(working.id, working.workingRevision);
        document = working.candidate?.status === 'processing' ? working.workingDocument : working.visibleVersion?.document ?? EMPTY;
        assets = working.assets;
      } else {
        const grid = await this.api.request('getSocialProfileGrid', { path: { userId: memberId } });
        document = grid.version?.document ?? EMPTY;
        assets = grid.assets;
      }
      await this.media.ensure([...mediaOf(document, assets), ...(profile.avatarAssetId ? [{ assetId: profile.avatarAssetId, variant: 'w640' as const }] : [])]);
      const doc: ProfileDoc = {
        name: profile.displayName, desc: profile.description ?? '', color: colourOf(profile), pic: pictureOf(profile, this.address),
        items: widgetsFromDocument(document, assets, this.address), lines: document.lines ?? 'colour',
      };
      return doc;
    });
  }

  /**
   * The member with this handle, or null (a page address names them: kalndar.com/<handle>, owner 4 October 2026).
   * The people search matches the start of handles in handle order, so an exact handle comes first; only an
   * exact match counts.
   */
  findMember(handle: string) {
    return this.call(async (): Promise<Member | null> => {
      const wanted = handle.trim().toLowerCase();
      if (!/^[a-z][a-z0-9_]{2,29}$/.test(wanted)) return null;
      const page = await this.api.request('searchSocialPeople', { query: { search: wanted, limit: 1 } });
      const found = page.items[0];
      if (!found || found.handle !== wanted) return null;
      if (found.avatarAssetId) await this.media.ensure([{ assetId: found.avatarAssetId, variant: 'w640' as const }]);
      return memberOf(found, this.me?.userId ?? null, this.address);
    });
  }

  // ------------------------------------------------------------ your grids

  /** Your daily grid for `date`: looked up, or created (with a remembered idempotency key) when `create`. */
  private ownGrid(date: ISODate, create: boolean): Promise<OwnGrid> {
    const known = this.grids.get(date);
    if (known) return Promise.resolve(known);
    const pending = this.gridLookups.get(date);
    if (pending) return pending;
    const lookup = (async () => {
      const found = await this.api.request('getOwnSocialDailyGrid', { query: { day: date } });
      let owner: SocialOwnerGrid | null = found.item && !found.item.deleted ? found.item : null;
      if (!owner) {
        if (!create) throw fail('not_found', 'no grid');
        let key = this.creationKeys.get(date);
        if (!key) { key = this.options.key(); this.creationKeys.set(date, key); }
        owner = await this.api.request('createSocialDailyGrid', { headers: { 'Idempotency-Key': key }, body: { expectedDayKey: date } });
      }
      const grid: OwnGrid = { id: owner.id, revision: owner.workingRevision };
      this.grids.set(date, grid);
      return grid;
    })();
    this.gridLookups.set(date, lookup);
    return lookup.finally(() => { if (this.gridLookups.get(date) === lookup) this.gridLookups.delete(date); });
  }

  private forgetGrid(date: ISODate): void { this.grids.delete(date); this.creationKeys.delete(date); }

  /** The working revision of a grid that is not a daily grid (your profile grid). */
  private async revisionOf(gridId: string): Promise<number> {
    const known = this.revisions.get(gridId);
    if (known !== undefined) return known;
    const working = await this.api.request('getSocialWorkingGrid', { path: { gridId } });
    this.revisions.set(gridId, working.workingRevision);
    return working.workingRevision;
  }

  /**
   * One change to a grid at its current working revision. A revision conflict (another tab, or a
   * revision this page had not seen) is retried once at the revision the backend reports.
   */
  private async atRevision<T>(gridId: string, revision: () => Promise<number>, remember: (value: number) => void, work: (revision: number) => Promise<T>): Promise<T> {
    return this.queue.run(gridId, async () => {
      const first = await revision();
      try { return await work(first); }
      catch (error) {
        if (codeOf(error) !== 'REVISION_CONFLICT') throw error;
        const current = currentRevisionOf(error);
        if (current === null) throw error;
        remember(current);
        return work(current);
      }
    });
  }

  private daily(date: ISODate) {
    return {
      revision: async () => (await this.ownGrid(date, true)).revision,
      remember: (value: number) => { const grid = this.grids.get(date); if (grid) grid.revision = value; },
    };
  }
  private other(gridId: string) {
    return { revision: () => this.revisionOf(gridId), remember: (value: number) => { this.revisions.set(gridId, value); } };
  }

  /** Save a working document, share it, and wait (up to 90 s) for the backend to publish it. */
  private async share(gridId: string, at: { revision: () => Promise<number>; remember: (value: number) => void }, document: SocialGridDocument, groupIds: readonly string[] | null): Promise<'published' | 'processing'> {
    const owner = await this.atRevision(gridId, at.revision, at.remember, (expectedRevision) =>
      this.api.request('saveSocialGridDraft', { path: { gridId }, body: { expectedRevision, document, ...(groupIds ? { groupIds: [...groupIds] } : {}) } }));
    at.remember(owner.workingRevision);
    const submission = await this.queue.run(gridId, () =>
      this.api.request('submitSocialGrid', { path: { gridId }, headers: { 'Idempotency-Key': this.options.key() }, body: { expectedRevision: owner.workingRevision } }));
    at.remember(owner.workingRevision + 1);
    return this.published(submission);
  }

  private async published(first: SocialSubmission): Promise<'published' | 'processing'> {
    const limit = this.options.publishWaitMs ?? 90_000, start = this.options.now();
    let submission = first, delay = 400;
    while (submission.status === 'processing') {
      if (this.options.now() - start >= limit) return 'processing';
      await this.options.sleep(delay);
      delay = Math.min(Math.round(delay * 1.6), 4_000);
      submission = await this.api.request('getSocialSubmission', { path: { submissionId: submission.id } });
    }
    if (submission.status === 'published') return 'published';
    if (submission.status === 'failed') throw fail('server', submission.errorCode ?? 'SUBMISSION_FAILED');
    throw fail('conflict', `submission ${submission.status}`);
  }

  savePost(date: ISODate, doc: PostDoc) {
    return this.call(async () => {
      const group = await this.everyone();
      const { document, missing } = documentFromWidgets(doc.items, doc.lines, this.options.key);
      if (missing) throw fail('validation', 'media without a backend id');
      let grid = await this.ownGrid(date, true), outcome: 'published' | 'processing';
      try { outcome = await this.share(grid.id, this.daily(date), document, [group.id]); }
      catch (error) {
        // Your grid was deleted elsewhere since this page saw it: start today's grid afresh, once.
        if (uiErrorOf(error).kind !== 'not_found') throw error;
        this.forgetGrid(date);
        grid = await this.ownGrid(date, true);
        outcome = await this.share(grid.id, this.daily(date), document, [group.id]);
      }
      this.months.clear();
      return this.readBack(document, doc.items, outcome === 'published' ? grid.id : null, group.id);
    });
  }

  /** The stored post: as the backend now shows it when published, else as saved (with the local previews). */
  private async readBack(document: SocialGridDocument, sent: readonly Widget[], gridId: string | null, groupId: string): Promise<PostDoc> {
    let stored = document, assets: readonly SocialMediaMetadata[] = [];
    if (gridId) {
      const read = await this.api.request('readSocialGrid', { path: { gridId }, query: { groupId } }).catch(() => null);
      if (read?.version) { stored = read.version.document; assets = read.assets; await this.media.ensure(mediaOf(stored, assets)); }
    }
    const byMedia = new Map(sent.filter((item) => item.mediaId).map((item) => [item.mediaId?.toLowerCase(), item]));
    const items = widgetsFromDocument(stored, assets, this.address).map((item) => {
      const local = item.mediaId ? byMedia.get(item.mediaId.toLowerCase()) : undefined;
      if (!local) return item;
      // Until the backend has measured the media, keep what the page measured.
      return { ...item, ...(item.ar === undefined && local.ar ? { ar: local.ar, kind: local.kind ?? item.kind } : {}), ...(item.len === undefined && local.len ? { len: local.len } : {}), url: item.url ?? local.url ?? null };
    });
    return { items, lines: stored.lines ?? 'colour' };
  }

  deletePost(date: ISODate) {
    return this.call(async () => {
      const found = await this.api.request('getOwnSocialDailyGrid', { query: { day: date } });
      const item = found.item;
      if (item && !item.deleted) {
        await this.atRevision(item.id, () => Promise.resolve(item.workingRevision), () => undefined, (expectedRevision) =>
          this.api.request('deleteSocialGrid', { path: { gridId: item.id }, query: { expectedRevision } }));
      }
      // The day's slot is free again; a new post needs a new grid and a new idempotency key.
      this.forgetGrid(date);
      this.months.clear();
    });
  }

  saveProfile(doc: ProfileDoc) {
    return this.call(async () => {
      const me = await this.api.request('socialMe', {});
      this.me = me;
      const profile = me.profile;
      if (!profile) throw fail('profile_required', 'PROFILE_REQUIRED');
      const name = doc.name.trim() || profile.displayName, colour = doc.color.toLowerCase(), avatar = avatarOf(doc.pic);
      const avatarChanged = !!avatar && JSON.stringify(avatar) !== JSON.stringify(profile.avatar ?? null);
      if (name !== profile.displayName || doc.desc !== (profile.description ?? '') || colour !== colourOf(profile) || avatarChanged) {
        await this.api.request('saveSocialProfile', { body: {
          expectedRevision: profile.revision, displayName: name, handle: profile.handle,
          postingTimezone: me.postingTimezone ?? this.options.timezone(), accentColor: colour, description: doc.desc,
          ...(avatar ? { avatar } : {}),
        } });
      }
      const { document, missing } = documentFromWidgets(doc.items, doc.lines ?? 'colour', this.options.key);
      if (missing) throw fail('validation', 'media without a backend id');
      const working = await this.api.request('getSocialWorkingGrid', { path: { gridId: profile.gridId } });
      this.revisions.set(working.id, working.workingRevision);
      if (!sameDocument(working.visibleVersion?.document, document) || working.candidate?.status === 'processing') {
        await this.share(profile.gridId, this.other(profile.gridId), document, null);
      }
      return this.getProfile(me.userId);
    });
  }

  /** Your colour, chosen at the first sign-in: always one profile save, so the revision moves past 1. */
  setColour(color: string) {
    return this.call(async () => {
      const me = await this.api.request('socialMe', {});
      this.me = me;
      const profile = me.profile;
      if (!profile) throw fail('profile_required', 'PROFILE_REQUIRED');
      this.me = await this.api.request('saveSocialProfile', { body: {
        expectedRevision: profile.revision, displayName: profile.displayName, handle: profile.handle,
        postingTimezone: me.postingTimezone ?? this.options.timezone(), accentColor: color.toLowerCase(),
      } });
    });
  }

  // ------------------------------------------------------------ uploads

  uploadMedia(file: File, purpose: 'post' | 'profile' | 'picture', onProgress?: (p: number) => void) {
    return this.call(async (): Promise<MediaRef> => {
      const me = this.requireMe();
      const type = file.type.toLowerCase(), image = IMAGE_TYPES.includes(type), video = VIDEO_TYPES.includes(type);
      if ((!image && !(video && purpose !== 'picture')) || (type === 'image/gif' && purpose === 'picture')) throw fail('unsupported', type || 'no type');
      if (file.size < 1) throw fail('validation', 'empty file');
      if (file.size > (image ? IMAGE_OPENED_MAX : VIDEO_MAX)) throw fail('too_large', `${file.size} bytes`);
      let status = await this.transferTurn(async () => {
        await this.cancelLeftBehind();
        // What is sent: the photo as the browser fitted it, or the picked file. The grid goes on showing the picked one.
        const sent = image && type !== 'image/gif' ? await this.fitted(file) : file;
        if (image && sent.size > IMAGE_MAX) throw fail('too_large', `${sent.size} bytes`);
        const body = { kind: image ? 'image' as const : 'video' as const, fileName: file.name || (image ? 'photo' : 'video'), declaredContentType: sent.type.toLowerCase(), declaredBytes: sent.size };
        let reserved: SocialUploadStatus;
        if (purpose === 'picture') {
          reserved = await this.placed(() => this.atRevision('avatar', () => Promise.resolve(me.profile.revision), () => undefined, (expectedRevision) =>
            this.api.request('reserveSocialAvatarUpload', { headers: { 'Idempotency-Key': this.options.key() }, body: { ...body, expectedRevision } })));
        } else {
          const today = this.today();
          const gridId = purpose === 'post' ? (await this.ownGrid(today, true)).id : me.profile.gridId;
          const at = purpose === 'post' ? this.daily(today) : this.other(gridId);
          reserved = await this.placed(() => this.atRevision(gridId, at.revision, at.remember, (expectedRevision) =>
            this.api.request('reserveSocialGridUpload', { path: { gridId }, headers: { 'Idempotency-Key': this.options.key() }, body: { ...body, expectedRevision } })));
        }
        if (reserved.sourceFrozenAt) return reserved;
        if (!['reserved', 'uploading'].includes(reserved.status)) throw fail('conflict', reserved.status);
        this.underWay.add(reserved.id); this.keepUploads();
        try {
          try { await this.options.transport.send(reserved.plan, sent, onProgress ?? (() => undefined), new AbortController().signal); }
          catch { throw fail('network', 'transfer'); }
          return await this.api.request('completeSocialUpload', { path: { uploadId: reserved.id }, body: {} });
        } catch (error) {
          // An upload that stopped keeps its place at the backend until it is cancelled or runs out half an hour later.
          // Three of those and every later file is refused, so the place is given back now. The failure is what is reported.
          this.leftBehind.push(reserved.id);
          await this.cancelLeftBehind();
          throw error;
        } finally { this.underWay.delete(reserved.id); this.keepUploads(); }
      });
      if (!status.sourceFrozenAt || !['processing', 'ready'].includes(status.status)) throw fail('server', status.errorCode ?? status.status);
      // A picture is chosen with the profile, which takes only a picture the backend has finished checking.
      if (purpose === 'picture') status = await this.ready(status);
      const preview = this.options.preview(file);
      if (preview) this.previews.set(status.assetId, preview);
      return { url: preview ?? '', mediaId: status.assetId, ...(file.name ? { name: file.name } : {}) };
    });
  }

  /** One of the transfer turns: at most `TRANSFERS_AT_ONCE` run, the others in the order they were asked for. */
  private async transferTurn<T>(run: () => Promise<T>): Promise<T> {
    // A turn that ends goes straight to the first one waiting, so the count goes down only when nobody waits.
    if (this.transfers < TRANSFERS_AT_ONCE) this.transfers += 1; else await new Promise<void>((go) => { this.transferTurns.push(go); });
    try { return await run(); } finally { const next = this.transferTurns.shift(); if (next) next(); else this.transfers -= 1; }
  }

  /** The photo as it is sent: fitted when the browser can, and only if that gives a smaller photo of a kind the backend takes. */
  private async fitted(file: File): Promise<File> {
    if (!this.options.fit) return file;
    try {
      const fitted = await this.options.fit(file), kind = fitted.type.toLowerCase();
      return fitted.size >= 1 && fitted.size < file.size && kind !== 'image/gif' && IMAGE_TYPES.includes(kind) ? fitted : file;
    } catch { return file; }
  }

  private keepUploads(): void { this.options.unfinishedUploads?.write([...this.leftBehind, ...this.underWay]); }

  /** Cancels the uploads that stopped without being cancelled. One the backend cannot be reached for stays for the next time. */
  private async cancelLeftBehind(): Promise<void> {
    const left = this.leftBehind.splice(0);
    if (!left.length) return;
    for (const uploadId of left) {
      // Any answer settles it: cancelled, already over, or not this member's. Only no answer at all is tried again.
      try { await this.api.request('cancelSocialUpload', { path: { uploadId }, body: {} }); }
      catch (error) { if (uiErrorOf(error).kind === 'network') this.leftBehind.push(uploadId); }
    }
    this.keepUploads();
  }

  /** A reservation, asked again for a while when the backend has no place for it yet. */
  private async placed(reserve: () => Promise<SocialUploadStatus>): Promise<SocialUploadStatus> {
    for (let attempt = 0; ; attempt += 1) {
      try { return await reserve(); }
      catch (error) {
        const wait = PLACE_WAITS_MS[attempt];
        if (codeOf(error) !== 'QUOTA_EXCEEDED' || wait === undefined) throw error;
        await this.options.sleep(wait);
      }
    }
  }

  private async ready(first: SocialUploadStatus): Promise<SocialUploadStatus> {
    const limit = this.options.publishWaitMs ?? 90_000, start = this.options.now();
    let status = first, delay = 400;
    while (status.status === 'processing') {
      if (this.options.now() - start >= limit) throw fail('server', 'upload still processing');
      await this.options.sleep(delay);
      delay = Math.min(Math.round(delay * 1.6), 4_000);
      status = await this.api.request('getSocialUpload', { path: { uploadId: status.id } });
    }
    if (status.status !== 'ready') throw fail(status.errorCode === 'UNSUPPORTED_MEDIA' ? 'unsupported' : 'validation', status.errorCode ?? status.status);
    return status;
  }

  /** Your current posting date, as the backend last said (each month read says which day is open to you). */
  private today(): ISODate {
    return this.todayKey ?? dayIn(this.options.now(), this.me?.postingTimezone ?? this.options.timezone());
  }

  // ------------------------------------------------------------ catalogues and session end

  listPresetPictures() {
    return this.call(async (): Promise<PresetPicture[]> => {
      this.appearance ??= await this.api.request('socialAppearance', {});
      // The backend's presets are white drawings meant to sit on the member colour, so they are not multiplied.
      return this.appearance.avatarPresets.map((preset) => ({ id: preset.id, name: preset.name, url: preset.url, blend: 'normal' as const }));
    });
  }

  async signOut(): Promise<void> {
    try { await this.options.purgeVoice(); } catch { /* the voice lab's device store may be unavailable; signing out goes on */ }
    let result: unknown;
    try { result = await this.options.auth.signOut(); } catch { throw fail('network', 'sign-out'); }
    if (result && typeof result === 'object' && (result as { error?: unknown }).error) throw fail('server', 'sign-out refused');
    this.reset();
  }
}

/** A chosen picture as the backend stores it, or null when the picture is not one it can store. */
function avatarOf(pic: Picture): SocialAvatarChoice | null {
  if (typeof pic === 'string') {
    const presets: readonly string[] = ['orbit', 'arch', 'leaf', 'peak', 'moon', 'spiral', 'ripple', 'tiles'];
    return presets.includes(pic) ? { kind: 'preset', presetId: pic as 'orbit' } : null;
  }
  return isUuid(pic.mediaId) ? { kind: 'upload', assetId: pic.mediaId.toLowerCase() } : null;
}

export interface V1Backend { readonly data: DataSource; readonly auth: AuthSource }

/** The v1 data source and sign-in on the real backend (design-handoff/07 §3). */
export function createV1BackendWith(options: V1BackendOptions): V1Backend {
  const data = new BackendDataSource(options);
  const auth = createV1Auth({ runtime: options.auth, api: options.api, google: options.google, timezone: options.timezone, changed: () => data.reset() });
  return { data, auth };
}
